import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardBody, CardHeader, Button, Textarea } from '../components/ui';
import { LinkifiedText } from '../components/LinkifiedText';
import { SessionDiscussionReplyLikeControl } from '../components/ReplyLikeControl';
import { SessionImageStrip, SessionSponsorDetailGroups } from '../components/SessionSponsorContent';
import { useAuth } from '../auth/AuthProvider';
import { sessionHasDetail, sessionRepliesEnabled } from '../lib/conference';
import { isCommitteeAdmin } from '../lib/admin';
import { isPresenterSupport, isSessionPresenter } from '../lib/sessionPresenters';
import {
  addSessionDiscussionReply,
  getConference,
  getDefaultConference,
  getConferenceSession,
  subscribeSessionDiscussionReplies,
} from '../lib/firestore';
import { trackEvent } from '../lib/analytics';
import { formatCompactDateTimeRange, formatDateTime, formatExactDateTime, formatRelativeDateTime, toDateSafe } from '../lib/dates';
import { memberLabel, memberPublicProfileUrl, useMemberDirectory } from '../lib/memberDirectory';
import { formatPersonName } from '../lib/names';
import type { Conference, Session, SessionDiscussionReply, SessionPollLink } from '../lib/types';

function groupThread(replies: SessionDiscussionReply[]) {
  const top: SessionDiscussionReply[] = [];
  const children: Record<string, SessionDiscussionReply[]> = {};
  for (const reply of replies) {
    if (!reply.parentReplyId) top.push(reply);
    else {
      children[reply.parentReplyId] = children[reply.parentReplyId] ?? [];
      children[reply.parentReplyId].push(reply);
    }
  }
  return { top, children };
}

function formatSessionRange(session: Session) {
  return formatCompactDateTimeRange(session.startAt, session.endAt);
}

function authorHasPublicProfile(profileId: string | undefined) {
  return Boolean(String(profileId || '').trim()) && !String(profileId || '').trim().startsWith('system:');
}

function SessionReplyAuthor(props: { profileId?: string; label: string }) {
  const profileId = String(props.profileId || '').trim();
  if (!authorHasPublicProfile(profileId)) {
    return (
      <span className="min-w-0 truncate text-xs text-zinc-700" title={props.label}>
        {props.label}
      </span>
    );
  }

  return (
    <a
      href={memberPublicProfileUrl(profileId)}
      className="min-w-0 truncate text-xs text-zinc-700 underline decoration-zinc-300 underline-offset-2 hover:text-zinc-900"
      title={props.label}
    >
      {props.label}
    </a>
  );
}

function pollAvailableFromMs(poll: SessionPollLink) {
  return toDateSafe(poll.availableFrom)?.getTime() ?? null;
}

function pollAvailableUntilMs(poll: SessionPollLink) {
  return toDateSafe(poll.availableUntil)?.getTime() ?? null;
}

function pollIsOpen(poll: SessionPollLink, nowMs: number) {
  const availableFrom = pollAvailableFromMs(poll);
  const availableUntil = pollAvailableUntilMs(poll);
  return (availableFrom == null || availableFrom <= nowMs) && (availableUntil == null || availableUntil > nowMs);
}

function attendeePollUrl(pollId: string) {
  return `/poll/p/${encodeURIComponent(pollId)}`;
}

function hostPollUrl(pollId: string) {
  return `/poll/host/${encodeURIComponent(pollId)}`;
}

function logoLinkProps(href: string) {
  if (/^https?:/i.test(href)) {
    return { target: '_blank', rel: 'noreferrer' };
  }
  return {};
}

function SessionLogoStrip(props: { logos: NonNullable<Session['vendorLogos']> }) {
  if (!props.logos.length) return null;

  return (
    <div
      className={[
        'mb-4 grid gap-3',
        props.logos.length > 1 ? 'grid-cols-2' : 'grid-cols-1 justify-items-center',
      ].join(' ')}
    >
      {props.logos.map((logo) => {
        const image = (
          <img
            src={logo.src}
            alt={logo.name}
            loading="lazy"
            className="h-full max-h-16 w-full max-w-52 object-contain"
          />
        );
        const className = [
          'flex h-20 min-w-0 items-center justify-center rounded-xl border border-zinc-200 bg-white px-3 py-2 shadow-sm',
          props.logos.length > 1 ? 'w-full' : 'w-full max-w-64',
        ].join(' ');

        return logo.href ? (
          <a key={`${logo.name}-${logo.src}`} href={logo.href} {...logoLinkProps(logo.href)} className={className}>
            {image}
          </a>
        ) : (
          <div key={`${logo.name}-${logo.src}`} className={className}>
            {image}
          </div>
        );
      })}
    </div>
  );
}

function SessionPollsPanel(props: { polls: SessionPollLink[]; canManage: boolean; nowMs: number }) {
  if (!props.polls.length) return null;

  return (
    <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-3">
      <div className="grid gap-2">
        {props.polls.map((poll) => {
          const open = pollIsOpen(poll, props.nowMs);
          const availableFrom = toDateSafe(poll.availableFrom);
          const availableUntil = toDateSafe(poll.availableUntil);
          const closed = availableUntil ? availableUntil.getTime() <= props.nowMs : false;
          return (
            <div key={poll.id} className="rounded-xl border border-zinc-200 bg-white p-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-zinc-950">{poll.title}</div>
                  {closed && availableUntil ? (
                    <div className="mt-1 text-xs text-zinc-600">
                      Closed at {formatDateTime(availableUntil)}
                    </div>
                  ) : !open && availableFrom ? (
                    <div className="mt-1 text-xs text-zinc-600">
                      Available from {formatDateTime(availableFrom)}
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {open ? (
                    <Link to={attendeePollUrl(poll.id)}>
                      <Button>Open</Button>
                    </Link>
                  ) : (
                    <Button disabled>Open</Button>
                  )}
                  {props.canManage ? (
                    <Link to={hostPollUrl(poll.id)}>
                      <Button variant="secondary">Host</Button>
                    </Link>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ConferenceSessionDiscussionPage() {
  const { conferenceId: routeConferenceId, sessionId } = useParams();
  const { user } = useAuth();
  const memberDirectory = useMemberDirectory();
  const [conference, setConference] = React.useState<Conference | null>(null);
  const [session, setSession] = React.useState<Session | null>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [replies, setReplies] = React.useState<SessionDiscussionReply[]>([]);
  const [body, setBody] = React.useState('');
  const [replyTo, setReplyTo] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  const trackedSessionRef = React.useRef('');
  const replyPanelRef = React.useRef<HTMLDivElement | null>(null);
  const resolvedConferenceId = conference?.id ?? session?.conferenceId ?? null;

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [routeConferenceId, sessionId]);

  React.useEffect(() => {
    if (!sessionId) return;

    let cancelled = false;
    setLoaded(false);
    setConference(null);
    setSession(null);
    setReplies([]);

    void (async () => {
      const nextConference = routeConferenceId ? await getConference(routeConferenceId) : await getDefaultConference();
      const nextSession = nextConference ? await getConferenceSession(nextConference.id, sessionId) : null;
      return { nextConference, nextSession };
    })()
      .then(({ nextConference, nextSession }) => {
        if (cancelled) return;
        setConference(nextConference);
        setSession(nextSession);
      })
      .catch(() => {
        if (cancelled) return;
        setConference(null);
        setSession(null);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [routeConferenceId, sessionId]);

  React.useEffect(() => {
    if (!resolvedConferenceId || !sessionId || !session || !sessionRepliesEnabled(session)) return;
    return subscribeSessionDiscussionReplies(resolvedConferenceId, sessionId, setReplies);
  }, [resolvedConferenceId, sessionId, session]);

  React.useEffect(() => {
    if (!resolvedConferenceId || !sessionId || !session || !sessionHasDetail(session)) return;
    const key = `${resolvedConferenceId}:${sessionId}`;
    if (trackedSessionRef.current === key) return;
    trackedSessionRef.current = key;
    void trackEvent('view_conference_session_discussion', {
      conference_id: resolvedConferenceId,
      session_id: sessionId,
    });
  }, [resolvedConferenceId, sessionId, session]);

  React.useEffect(() => {
    const futureTimes = (session?.polls ?? [])
      .flatMap((poll) => [pollAvailableFromMs(poll), pollAvailableUntilMs(poll)])
      .filter((value): value is number => typeof value === 'number' && value > nowMs)
      .sort((a, b) => a - b);

    const next = futureTimes[0];
    if (!next) return;

    const timeout = window.setTimeout(
      () => setNowMs(Date.now()),
      Math.min(next - nowMs, 2_147_483_647)
    );
    return () => window.clearTimeout(timeout);
  }, [session?.polls, nowMs]);

  async function submit() {
    if (!resolvedConferenceId || !sessionId || !session || !sessionRepliesEnabled(session)) return;
    if (!body.trim() || !user?.sub || submitting) return;

    setSubmitting(true);
    setError(null);

    try {
      const trimmedBody = body.trim();
      const payload: any = {
        conferenceId: resolvedConferenceId,
        sessionId,
        body: trimmedBody,
        authorProfileId: user.sub,
        authorDisplayName: formatPersonName(user.displayName),
      };

      if (replyTo) payload.parentReplyId = replyTo;

      await addSessionDiscussionReply(payload);
      await trackEvent('post_conference_session_reply', {
        conference_id: resolvedConferenceId,
        session_id: sessionId,
        reply_target: replyTo ? 'reply' : 'session',
        body_length: trimmedBody.length,
      });

      setBody('');
      setReplyTo(null);
    } catch (e: any) {
      console.error(e);
      setError(e?.message ? String(e.message) : 'Failed to post reply');
    } finally {
      setSubmitting(false);
    }
  }

  function scrollToReplyPanel() {
    window.setTimeout(() => {
      const panel = replyPanelRef.current;
      if (!panel) return;

      const header = document.querySelector('header');
      const headerHeight = header instanceof HTMLElement ? header.getBoundingClientRect().height : 0;
      const top = panel.getBoundingClientRect().top + window.scrollY - headerHeight - 12;
      window.scrollTo({ top: Math.max(0, top), left: 0, behavior: 'smooth' });
    }, 0);
  }

  function startReplyTo(replyId: string) {
    setReplyTo(replyId);
    scrollToReplyPanel();
  }

  if (!sessionId) {
    return <div className="text-sm text-zinc-600">Missing conference session</div>;
  }

  if (loaded && (!session || !sessionHasDetail(session))) {
    return (
      <div className="grid grid-cols-1 gap-4">
        <Card>
          <CardBody className="space-y-3">
            <div className="text-sm text-zinc-700">This session discussion is not available.</div>
            <Link to="/conference">
              <Button variant="secondary">Back to conference</Button>
            </Link>
          </CardBody>
        </Card>
      </div>
    );
  }

  const { top, children } = groupThread(replies);
  const repliesEnabled = session ? sessionRepliesEnabled(session) : false;
  const sessionPolls = session?.polls ?? [];
  const canManagePolls = Boolean(
    session && (isCommitteeAdmin(user) || isPresenterSupport(user, resolvedConferenceId) || isSessionPresenter(session, user))
  );

  return (
    <div className="grid grid-cols-1 gap-4">
      <Card>
        <CardHeader
          title={session ? session.title : 'Session discussion'}
          subtitle={conference ? conference.name : undefined}
          right={
            <Link to="/conference">
              <Button variant="secondary">Back</Button>
            </Link>
          }
        />
        <CardBody className="space-y-4">
          {session ? (
            <div className="rounded-2xl border border-zinc-200 bg-white p-3">
              {session.vendorLogos?.length && !session.sponsorGroups?.length ? <SessionLogoStrip logos={session.vendorLogos} /> : null}
              {session.sessionImages?.length ? <SessionImageStrip images={session.sessionImages} /> : null}
              {session.abstract ? (
                <div className={['whitespace-pre-wrap text-sm text-zinc-800', session.sessionImages?.length ? 'mt-4' : ''].join(' ')}>
                  <LinkifiedText text={session.abstract} />
                </div>
              ) : null}
              {session.sponsorGroups?.length ? (
                <div className={session.abstract ? 'mt-4' : ''}>
                  <SessionSponsorDetailGroups groups={session.sponsorGroups} />
                </div>
              ) : null}
              <div className="mt-2 text-xs text-zinc-600">
                {formatSessionRange(session)}
                {session.room ? ` | ${session.room}` : ''}
              </div>
            </div>
          ) : (
            <div className="text-sm text-zinc-600">Loading</div>
          )}

          {sessionPolls.length ? (
            <SessionPollsPanel polls={sessionPolls} canManage={canManagePolls} nowMs={nowMs} />
          ) : null}

          {repliesEnabled ? (
            <div className="space-y-3">
              {top.map((reply) => {
                const author = memberLabel(reply.authorProfileId, reply.authorDisplayName, memberDirectory);
                return (
                  <div key={reply.id} className="rounded-2xl border border-zinc-200 bg-white p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1 whitespace-pre-wrap text-sm text-zinc-800">
                        <LinkifiedText text={reply.body} />
                      </div>
                      <Button variant="ghost" onClick={() => startReplyTo(reply.id)}>
                        Reply
                      </Button>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <SessionReplyAuthor profileId={reply.authorProfileId} label={author} />
                      <div className="flex shrink-0 items-center gap-1.5 text-xs text-zinc-600">
                        {resolvedConferenceId ? (
                          <SessionDiscussionReplyLikeControl
                            conferenceId={resolvedConferenceId}
                            sessionId={sessionId}
                            replyId={reply.id}
                            profileId={user?.sub}
                            likeCount={reply.likeCount}
                          />
                        ) : null}
                        <span title={formatExactDateTime(reply.updatedAt || reply.createdAt)}>
                          Updated {formatRelativeDateTime(reply.updatedAt || reply.createdAt)}
                        </span>
                      </div>
                    </div>

                    {children[reply.id]?.length ? (
                      <div className="mt-3 space-y-2 border-l border-zinc-200 pl-3">
                        {children[reply.id].map((child) => {
                          const childAuthor = memberLabel(child.authorProfileId, child.authorDisplayName, memberDirectory);
                          return (
                            <div key={child.id} className="rounded-xl border border-zinc-200 bg-zinc-50 p-3">
                              <div className="whitespace-pre-wrap text-sm text-zinc-800">
                                <LinkifiedText text={child.body} />
                              </div>
                              <div className="mt-2 flex items-center justify-between">
                                <SessionReplyAuthor profileId={child.authorProfileId} label={childAuthor} />
                                <div className="flex shrink-0 items-center gap-1.5 text-xs text-zinc-600">
                                  {resolvedConferenceId ? (
                                    <SessionDiscussionReplyLikeControl
                                      conferenceId={resolvedConferenceId}
                                      sessionId={sessionId}
                                      replyId={child.id}
                                      profileId={user?.sub}
                                      likeCount={child.likeCount}
                                    />
                                  ) : null}
                                  <span title={formatExactDateTime(child.updatedAt || child.createdAt)}>
                                    Updated {formatRelativeDateTime(child.updatedAt || child.createdAt)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : null}

          {repliesEnabled ? (
            <div ref={replyPanelRef} className="rounded-2xl border border-zinc-200 bg-zinc-50 p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="text-xs font-semibold text-zinc-900">
                  Reply {replyTo ? '(replying to a message)' : ''}
                </div>
                <div className="flex items-center gap-2">
                  {replyTo ? (
                    <Button variant="ghost" onClick={() => setReplyTo(null)}>
                      Cancel reply-to
                    </Button>
                  ) : null}
                  <Button onClick={submit} disabled={!body.trim() || submitting}>
                    {submitting ? 'Posting...' : 'Post reply'}
                  </Button>
                </div>
              </div>
              <Textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write a reply" />
              {error ? (
                <div className="mt-2 rounded-xl border border-red-200 bg-red-50 p-2 text-xs text-red-800">{error}</div>
              ) : null}
            </div>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}
